import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Flag, AlertTriangle } from "lucide-react";

/**
 * AuthCallback — Emergent Google Auth lands the user at
 * `/auth/callback#session_id=...`. This page extracts that fragment,
 * forwards it to `/api/auth/google-callback` (which exchanges it
 * server-side for a Google profile + sets our session cookies), then
 * reloads the auth context and routes new users to /intake (to pick
 * account type) or returning users to /dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { reload } = useAuth();
  const [error, setError] = useState("");
  // useRef guard prevents StrictMode's double-mount from double-exchanging
  // the one-time session_id (Emergent invalidates it after first use).
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : "";
    if (!sessionId) {
      setError("Missing session ID from Google. Please try again.");
      return;
    }

    (async () => {
      try {
        const resp = await api.post("/auth/google-callback", { session_id: sessionId });
        const isNew = resp.headers?.["x-auth-is-new"] === "1";
        // Clean the fragment before navigating so we don't leave the
        // one-time session_id in history.
        try { window.history.replaceState({}, "", window.location.pathname); } catch {} // eslint-disable-line
        await reload();
        navigate(isNew ? "/intake" : "/dashboard", { replace: true });
      } catch (e) {
        setError(
          formatApiErrorDetail(e.response?.data?.detail) ||
          e.message ||
          "Google sign-in failed. Please try again.",
        );
      }
    })();
  }, [navigate, reload]);

  return (
    <div className="min-h-screen bg-flag-ivory flex flex-col items-center justify-center px-4" data-testid="auth-callback">
      <div className="flex items-center gap-2 font-display font-black text-2xl text-flag-navy mb-6">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-flag-red text-white">
          <Flag className="h-4 w-4" />
        </span>
        Electable
      </div>
      {!error ? (
        <div className="flex items-center gap-3 text-slate-700" data-testid="auth-callback-loading">
          <Loader2 className="h-5 w-5 animate-spin text-flag-red" />
          <span className="font-semibold">Signing you in with Google…</span>
        </div>
      ) : (
        <div className="max-w-md w-full bg-white border-2 border-red-200 rounded-xl shadow-sm p-6 text-center" data-testid="auth-callback-error">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-flag-red/10 text-flag-red mb-3">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <h1 className="font-display text-lg font-bold text-flag-navy">Couldn't finish sign-in</h1>
          <p className="mt-2 text-sm text-slate-700">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/register", { replace: true })}
            data-testid="auth-callback-retry-btn"
            className="mt-4 inline-flex h-11 px-5 items-center rounded-md bg-flag-red hover:bg-flag-red-hover text-white font-bold"
          >
            Back to sign up
          </button>
        </div>
      )}
    </div>
  );
}
